import { Flex, Radio, Table, Typography, Row, Col, Input } from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../../common/spinner/Spinner";
import {
  dateObjToDispViewDate,
  timeObjToDispViewTimeWithSuffix,
} from "../../../../../../utils/utils";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { getWalletTransactionsAction } from "../../../../../../redux/services/wallet-transactions/walletTransactionsServices";
import { SearchOutlined } from "@ant-design/icons";
import styles from "./WalletTransactions.module.scss";
import { userRoles } from "../../../../../../constants";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import useDebounce from "../../../../../../hooks/useDebounce";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.guess();

const WalletTransactions = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [searchValue, setSearchValue] = useState("");
  const [filterRole, setFilterRole] = useState(userRoles.CUSTOMER);
  const [columns, setColumns] = useState([]);
  const { walletTransactions } = useSelector(
    (state) => state.walletTransactions
  );
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: walletTransactions?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* function definition to get table data */

  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );

  /* function definition to get table data */
  const handleGetTableData = () => {
    setLoading(true);
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      role: filterRole
    };
    dispatch(getWalletTransactionsAction(dataToBeSent)).then(() => {
      setLoading(false);
    });
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue, filterRole]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("user"),
        render: (_, record) =>
          record?.user?.role === userRoles.SERVICE_PROVIDER_ADMIN
            ? record?.organization?.name
            : record?.user?.first_name || record?.user?.first_name
            ? `${record?.user?.first_name} ${record?.user?.last_name}`
            : "-",
      },
      {
        title: t("walletInfo.transactionDate"),
        dataIndex: "transaction_date",
        key: "transaction_date",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.transaction_date, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("walletInfo.transactionTime"),
        dataIndex: "transaction_date",
        key: "transaction_date",
        render: (_, record) => (
          <p>
            {timeObjToDispViewTimeWithSuffix(
              dayjs
                .utc(record?.transaction_date)
                .local()
                .tz(dayjs.tz.guess())
                .format("YYYY-MM-DDTHH:mm:ss")
            )}
            {/* {timeObjToDispViewTimeWithSuffix(dayjs(record?.transaction_date).subtract(dayjs(record?.transaction_date).utcOffset(),"minute").format("YYYY-MM-DDTHH:mm:ss"))} */}
          </p>
        ),
      },
      {
        title: t("walletInfo.withdrawal"),
        dataIndex: "withdrawal",
        key: "withdrawal",
        render: (_, record) => (
          <p>{record?.withdrawal ? record?.withdrawal?.toFixed(2) : 0}</p>
        ),
      },
      {
        title: t("walletInfo.deposit"),
        dataIndex: "deposit",
        key: "deposit",
        render: (_, record) => (
          <p>{record?.deposit ? record?.deposit?.toFixed(2) : 0}</p>
        ),
      },
      {
        title: t("walletInfo.closing"),
        dataIndex: "closing_balance",
        key: "closing_balance",
        render: (_, record) => (
          <p>{Number(record?.closing_balance)?.toFixed(4)}</p>
        ),
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  return (
    <div className="servicesListContainer">
      <Flex vertical gap="2rem">
        <Flex align="center" justify="space-between">
          <Typography.Title level={2}>{t("walletInfo.walletTransactions")}</Typography.Title>
          <Radio.Group
            value={filterRole}
            onChange={(event) => setFilterRole(event.target.value)}
          >
            <Radio value={userRoles.CUSTOMER}>
              {t("Modules.WalletManagement.customer")}
            </Radio>
            <Radio value={userRoles.SERVICE_PROVIDER_ADMIN}>
              {t("Modules.WalletManagement.serviceProvider")}
            </Radio>
          </Radio.Group>
        </Flex>
        <Row gutter={24} justify="space-between">
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("CommonUtils.searchByName")}
              onChange={(event) => setSearchValue(event.target.value)}
              style={{ width: "370px" }}
            />
          </Col>
        </Row>
        {loading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={walletTransactions?.data?.filter(
              (element) => element?.user?.role === filterRole
            )}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default WalletTransactions;
